import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { IUser, IAuthenticatedUser, Providers, Provider } from './user.entity';
import { INormalizedPayload } from '../state/entities/entity';
import { InvestigatorEntityKeys } from '@core/state/entities/investigator/investigator.entity';
import { ProjectUsageEntityKeys } from '@core/state/entities/project-usage/project-usage.entity';
import {
  ChangeEmailError,
  CodeVerificationError,
  CreateUserDto,
  FullName,
  AccountManagementDto,
  NewUserCreatedDto,
  ProfileUpdatedDto,
} from '@core/services/api-client.generated';
import { IAuthAppState } from '@core/auth/auth.reducer';
import { AccountDeletionError } from './account-deletion-error';
// Removed direct pdfjs-dist import to avoid CommonJS bundling.
import {
  IErrorStateWithCorrelation,
  IRequestCorrelationState,
} from '@core/actions/project-detail.actions';
import { entityKey, EntityKeys } from '@core/state/entities/entity-name-map';

/** Why the admin user-list read failed: the API refused ListUsers, or anything else went wrong. */
export type InvestigatorsLoadFailure = 'denied' | 'error';

export const authApiActions = createActionGroup({
  source: 'Auth Api',
  events: {
    'Deactivate Account': emptyProps(),
    'Account Deactivated': emptyProps(),
    'Account Deactivation Failed': props<{ reason: AccountDeletionError }>(),
    'Delete Account': emptyProps(),
    'Account Deleted': emptyProps(),
    'Clear Account Deletion Error': emptyProps(),
    'Account Deletion Failed': props<{ reason: AccountDeletionError }>(),
    'Update Primary Email': props<
      { email: string } & IRequestCorrelationState
    >(),
    'Silently Refresh Token': emptyProps(),
    'Primary Email Updated': props<
      ProfileUpdatedDto & IRequestCorrelationState
    >(),
    'Primary Email Update Failed': props<
      {
        error: unknown;
        errorCode: ChangeEmailError | null;
      } & IRequestCorrelationState
    >(),
    'Send Add New Email Verification Email': props<{ newEmail: string }>(),
    'Add New Email Verification Email Sent':
      props<INormalizedPayload<InvestigatorEntityKeys>>(),
    'Add New Email Verification Email Send Failed': props<{
      error: unknown;
      errorCode: ChangeEmailError | null;
    }>(),
    'Verify Add Profile Email': props<{ code: number; newEmail: string }>(),
    'Profile Email Verification Failed': props<{
      error: any;
      errorCode: CodeVerificationError | null;
    }>(),
    'Profile Email Verified And Added': props<ProfileUpdatedDto>(),
    'Remove Email From Profile': props<
      { email: string } & IRequestCorrelationState
    >(),
    'Email Removed From Profile': props<
      ProfileUpdatedDto & IRequestCorrelationState
    >(),
    'Error Removing Email From Profile': props<IErrorStateWithCorrelation>(),
    'Send Password Reset Email': emptyProps(),
    'Password Email Reset Sent': emptyProps(),
    'Password Email Reset Failed': props<{ error: any }>(),
    'Update Profile Name': props<{ fullname: FullName }>(),
    'Profile Name Updated': props<ProfileUpdatedDto>(),
    'Profile Picture Updated': props<ProfileUpdatedDto>(),
    'Load Account Management': emptyProps(),
    'Account Management Received': props<AccountManagementDto>(),
    'Account Management Failed': emptyProps(),
    /**
     * The account-management request still answered 401 after the interceptor's
     * refresh and silent restore (cross-cutting rule 8): the session is ending,
     * which is not the same as "status unavailable".
     */
    'Account Management Session Ended': emptyProps(),
    'Connect Social Sign In': props<{ provider: Provider }>(),
    'Disconnect Social Sign In': props<{ provider: Provider }>(),
    'Get Management Access Token': emptyProps(),
    'Management Access Token Received': props<{ token: string }>(),
    'Management Access Token Failed': props<{ error: any }>(),
    'Auth State Processed': emptyProps(),
    'Auth State Received': props<{ appState: IAuthAppState }>(),
    'User Sign Up Succeeded': props<NewUserCreatedDto>(),
    'Start Sign Up': props<CreateUserDto>(),
    'User Sign Up Failed': props<{ error: string; statusCode: number }>(),
    'Loading User Complete': emptyProps(),
    'Sign In Recorded': props<
      INormalizedPayload<ProjectUsageEntityKeys | InvestigatorEntityKeys> &
        IRequestCorrelationState
    >(),
    'Start User Impersonation': props<{ userToImpersonate: IUser }>(),
    'Stop User Impersonation': emptyProps(),
    'Id Token Received': props<{
      user: IAuthenticatedUser;
      impersonating: IUser | null;
      idToken: string;
    }>(),
    'Authentication Error': props<{
      message: string;
      object?: any;
      handled?: boolean;
    }>(),
    'Silent Sign Out': emptyProps(),
    'Sign Out': props<{ onlyLocal: boolean; noRedirect?: boolean }>(),
    'Loading User': emptyProps(),
    'Start Sign In Popup': emptyProps(),
    'Start Explicit Sign In Redirect': emptyProps(),
    'Start Sign In': props<{
      socialEmail?: string;
      login_hint?: string;
      screen_hint?: 'signup' | 'login';
      linkSocial?: Providers;
      popup?: boolean;
      redirectUrl?: string | null;
      connection?: 'google-oauth2' | 'Username-Password-Authentication';
      prompt?: 'none' | 'login' | 'consent' | 'select_account';
    }>(),
    'Start Registration Redirect': props<{ redirectUrl: string | null }>(),
    'Signed Out By Identity Provider': emptyProps(),
    'Load Investigators': emptyProps(),
    'Investigators Loaded':
      props<INormalizedPayload<typeof entityKey.adminInvestigators>>(),
    /** The user-list read failed: `denied` is the API refusing ListUsers (403), `error` anything else. */
    'Investigators Load Failed': props<{ reason: InvestigatorsLoadFailure }>(),
  },
});
